import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';

// Login — rediseño visual del formulario existente. La lógica de auth,
// validaciones, Google sign-in y routing NO se modifica: solo la cáscara
// visual se adapta al mockup (dos estados: light y dark). Acento azul
// institucional (#2563EB) inline para no tocar tokens globales del app.
export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((e) => e.msg || JSON.stringify(e)).join(' '));
      } else {
        setError('Error al iniciar sesión');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="login-page"
      className="min-h-screen flex items-center justify-center p-4 sm:p-6 bg-[#F3F4F6] dark:bg-[#0a0f1c]"
    >
      <div className="w-full max-w-md">
        <div
          data-testid="login-card"
          className="bg-white dark:bg-[#0f1729] rounded-[28px] shadow-[0_10px_40px_-10px_rgba(15,23,42,0.15)] dark:shadow-[0_10px_40px_-10px_rgba(0,0,0,0.5)] ring-1 ring-slate-200/60 dark:ring-white/10 p-8 sm:p-10"
        >
          {/* Logo: casa institucional en azul */}
          <div className="flex items-center justify-center mb-6">
            <Home
              className="w-16 h-16 text-[#2563EB] dark:text-[#3B82F6]"
              strokeWidth={1.8}
            />
          </div>

          {/* Título + subtítulo */}
          <h1 className="text-3xl sm:text-4xl font-extrabold text-center text-slate-900 dark:text-white tracking-tight">
            Vida en Orden
          </h1>
          <p className="text-sm sm:text-base text-center text-slate-500 dark:text-slate-400 mt-2 mb-8">
            Iniciá sesión en tu cuenta
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div
                data-testid="login-error"
                className="bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 text-red-700 dark:text-red-300 px-4 py-3 rounded-xl text-sm"
              >
                {error}
              </div>
            )}

            {/* Email */}
            <div>
              <label
                htmlFor="login-email"
                className="block text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2"
              >
                Email
              </label>
              <div className="relative">
                <Mail
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-slate-500 pointer-events-none"
                  strokeWidth={1.8}
                />
                <input
                  id="login-email"
                  data-testid="login-email-input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-12 pr-4 py-3.5 rounded-2xl bg-slate-100/70 dark:bg-white/5 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 border border-transparent dark:border-white/5 focus:outline-none focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6] focus:bg-white dark:focus:bg-white/10 transition-all"
                  placeholder="tu@email.com"
                  required
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label
                htmlFor="login-password"
                className="block text-sm font-semibold text-slate-800 dark:text-slate-200 mb-2"
              >
                Contraseña
              </label>
              <div className="relative">
                <Lock
                  className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 dark:text-slate-500 pointer-events-none"
                  strokeWidth={1.8}
                />
                <input
                  id="login-password"
                  data-testid="login-password-input"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-12 py-3.5 rounded-2xl bg-slate-100/70 dark:bg-white/5 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500 border border-transparent dark:border-white/5 focus:outline-none focus:ring-2 focus:ring-[#2563EB] dark:focus:ring-[#3B82F6] focus:bg-white dark:focus:bg-white/10 transition-all"
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  data-testid="toggle-password-btn"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-full text-slate-400 dark:text-slate-500 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-white/10 transition-colors"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" strokeWidth={1.8} />
                  ) : (
                    <Eye className="w-5 h-5" strokeWidth={1.8} />
                  )}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full py-3.5 mt-2 rounded-full text-white font-semibold shadow-lg shadow-[#2563EB]/25 dark:shadow-[#3B82F6]/25 flex items-center justify-center gap-2 transition-all bg-[#2563EB] hover:bg-[#1E4FCF] dark:bg-[#3B82F6] dark:hover:bg-[#2563EB] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <span>{loading ? 'Iniciando...' : 'Iniciar sesión'}</span>
              {!loading && <ArrowRight className="w-5 h-5" strokeWidth={2} />}
            </button>
          </form>

          {/* Divider */}
          <div className="my-6 flex items-center gap-4">
            <div className="flex-1 h-px bg-slate-200 dark:bg-white/10" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-400 dark:text-slate-500">
              o continuá con
            </span>
            <div className="flex-1 h-px bg-slate-200 dark:bg-white/10" />
          </div>

          {/* Google button */}
          <button
            data-testid="google-signin-btn"
            type="button"
            onClick={loginWithGoogle}
            className="w-full py-3.5 rounded-full bg-white dark:bg-white/5 text-slate-800 dark:text-slate-100 font-semibold border border-slate-200 dark:border-white/10 hover:bg-slate-50 dark:hover:bg-white/10 transition-all flex items-center justify-center gap-3"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span>Continuar con Google</span>
          </button>

          {/* Registro */}
          <p className="mt-6 text-center text-sm text-slate-500 dark:text-slate-400">
            ¿No tenés cuenta?{' '}
            <Link
              to="/register"
              data-testid="register-link"
              className="text-[#2563EB] dark:text-[#3B82F6] font-semibold hover:underline"
            >
              Registrate aquí
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
